/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "../../../../components/MDBox";
import MDTypography from "../../../../components/MDTypography";
import MDAvatar from "../../../../components/MDAvatar";

// Material Dashboard 2 React base styles
import breakpoints from "../../../../assets/theme/base/breakpoints";

// Images
import burceMars from "../../../../assets/images/bruce-mars.jpg";
import backgroundImage from "../../../../assets/images/landing-10.png";
import { useDispatch, useSelector } from "react-redux";
import { properlizeWords } from "../../../../helpers";
import {
  AccessibleForward,
  BorderColor,
  Check,
  Lock as LockIcon,
  Person,
  ReportProblem,
  Settings,
  TrendingUp,
} from "@mui/icons-material";
import { setMainProfileTab } from "../../../../stateManagement/reducers/profileReducer";
import {
  CircularProgress,
  ListItemButton,
  Stack,
  useTheme,
} from "@mui/material";
import {
  clearUpdateUserErrs,
  resetUpdatedUser,
  tryUpdateUser,
} from "../../../../stateManagement/reducers/usersReducer";
import MDSnackbar from "../../../../components/MDSnackbar";
import { tryConfirmLogin } from "../../../../stateManagement/reducers/authReducer";
import PopUp from "../../../../components/PopUp/PopUp";

function Header({ children }) {
  const dispatch = useDispatch();
  const { user, authToken, confirmingLogin } = useSelector(
    (state) => state.auth,
  );
  const { updatingUser, updatedUser, updateUserErrs } = useSelector(
    (state) => state.users,
  );
  const theme = useTheme();

  const { profileTabs, mainProfileTab } = useSelector((state) => state.profile);

  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [tabValue, setTabValue] = useState(0);

  const [imgSrc, setImgSrc] = useState(
    user?.imgSrc
      ? `${process.env.REACT_APP_SERVER_ENDPOINT}/${user?.imgSrc}`
      : null,
  );

  const [formSuccess, setFormSuccess] = useState(null);
  const [formErr, setFormErr] = useState(null);

  const closeFormSuccesss = () => setFormSuccess(null);
  const closeFormErr = () => setFormErr(null);

  const renderFormErr = (
    <MDSnackbar
      color="error"
      icon={<ReportProblem color="error" />}
      title="Something Went Wrong"
      content={formErr ?? ""}
      dateTime="Just now"
      open={Boolean(formErr)}
      onClose={closeFormErr}
      close={closeFormErr}
      bgWhite
      sx={{
        position: "fixed",
        top: "1rem",
      }}
    />
  );

  const renderFormSuccess = (
    <MDSnackbar
      color="success"
      icon={<Check color="success" />}
      title="Profile-Update Successful"
      content={formSuccess}
      dateTime="Just now"
      open={Boolean(formSuccess)}
      onClose={closeFormSuccesss}
      close={closeFormSuccesss}
      bgWhite
    />
  );

  const [isInitRender1, setIsInitRender1] = useState(true);
  useEffect(() => {
    if (isInitRender1) {
      setFormSuccess(null);
      setIsInitRender1(false);
      dispatch(resetUpdatedUser());
      return;
    }

    if (updatedUser) {
      dispatch(tryConfirmLogin({ authToken: authToken ?? "" }));
      setFormSuccess("The changes have been saved.");
    } else {
      setFormErr(null);
    }
  }, [updatedUser]);

  const [isInitRender2, setIsInitRender2] = useState(true);
  useEffect(() => {
    if (isInitRender2) {
      dispatch(clearUpdateUserErrs());
      setFormErr(null);
      setIsInitRender2(false);
      return;
    }

    if (updateUserErrs.form) {
      setFormErr(updateUserErrs?.usrImg ?? updateUserErrs.form);
    } else {
      setFormErr(null);
    }
  }, [updateUserErrs.form]);

  const handleImgChange = (file) => {
    const reader = new FileReader();
    const { files } = file.target;
    if (files && files.length !== 0) {
      reader.onload = () => setImgSrc(reader.result);

      reader.readAsDataURL(files[0]);

      const data = new FormData();

      if (file.target.files[0].size > 0) {
        data.append("usrImg", file.target.files[0]);
      }

      dispatch(
        tryUpdateUser({
          authToken: authToken ?? "",
          userId: user?._id ?? "",
          data,
        }),
      );
    }
  };

  useEffect(() => {
    if (user?.imgSrc) {
      setImgSrc(`${process.env.REACT_APP_SERVER_ENDPOINT}/${user?.imgSrc}`);
    }
  }, [user?.imgSrc]);

  useEffect(() => {
    // A function that sets the orientation state of the tabs.
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }

    /** 
     The event listener that's calling the handleTabsOrientation function when resizing the window.
    */
    window.addEventListener("resize", handleTabsOrientation);

    // Call the handleTabsOrientation function to set the state with the initial value.
    handleTabsOrientation();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  const handleSetTabValue = (event, newValue) => setTabValue(newValue);

  useEffect(() => {
    if (profileTabs.length > 0) {
      dispatch(setMainProfileTab(profileTabs[0]));
    }
  }, []);

  return (
    <>
      <MDBox position="relative">
        <MDBox
          display="flex"
          alignItems="center"
          position="relative"
          minHeight="18.75rem"
          borderRadius="xl"
          sx={{
            backgroundImage: ({
              functions: { rgba, linearGradient },
              palette: { gradients },
            }) =>
              `${linearGradient(
                rgba(gradients.info.main, 0),
                rgba(gradients.info.state, 0),
              )}, url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "50%",
            overflow: "hidden",
          }}
        />
        <Card
          sx={{
            position: "relative",
            mt: -8,
            mx: 3,
            py: 2,
            px: 2,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Stack alignItems="center" sx={{ width: "fit-content" }}>
                <MDAvatar
                  src={imgSrc}
                  alt="profile-image"
                  size="xl"
                  shadow="sm"
                  sx={{
                    "& img": {
                      width: "100% !important",
                      height: "100% !important",
                      objectFit: "cover !important",
                    },
                    bgcolor: theme.palette.secondary.main,
                  }}
                >
                  <Person color="white" sx={{ height: "70%", width: "70%" }} />
                </MDAvatar>
                <ListItemButton
                  sx={{ position: "relative", cursor: "pointer" }}
                >
                  <MDTypography
                    sx={{ fontSize: ".9rem", color: theme.palette.info.main }}
                  >
                    <u>Edit</u>
                  </MDTypography>
                  <input
                    type="file"
                    onChange={handleImgChange}
                    accept="image/png, image/jpeg"
                    id="account-settings-upload-image"
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      width: "100%",
                      height: "100%",
                      opacity: "0%",
                      cursor: "pointer",
                    }}
                  />
                </ListItemButton>
              </Stack>
            </Grid>
            <Grid item>
              <MDBox height="100%" mt={-5} lineHeight={1}>
                <MDTypography variant="h5" fontWeight="medium">
                  {user?.preferredName ??
                    properlizeWords(
                      (user?.firstName ?? "") + " " + (user?.lastName ?? ""),
                    )}
                </MDTypography>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                >
                  {properlizeWords(user?.role?.name ?? "")}
                </MDTypography>
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4} sx={{ ml: "auto" }}>
              <AppBar position="static">
                <Tabs
                  orientation={tabsOrientation}
                  value={tabValue}
                  onChange={handleSetTabValue}
                >
                  {profileTabs.map((tab, index) => {
                    return (
                      <Tab
                        key={index}
                        label={tab.label}
                        icon={tab.icon}
                        onClick={() => {
                          dispatch(setMainProfileTab(tab));
                        }}
                      />
                    );
                  })}
                </Tabs>
              </AppBar>
            </Grid>
          </Grid>
          {children}
        </Card>
      </MDBox>
      {renderFormErr}
      {renderFormSuccess}
      {(updatingUser || confirmingLogin) && (
        <PopUp
          id="update-user-PopUp"
          notCloseable
          deployed={updatingUser || confirmingLogin}
          setDeployed={(truth) => {}}
        >
          <MDBox>
            <CircularProgress color="info" />
          </MDBox>
        </PopUp>
      )}
    </>
  );
}

// Setting default props for the Header
Header.defaultProps = {
  children: "",
};

// Typechecking props for the Header
Header.propTypes = {
  children: PropTypes.node,
};

export default Header;
